import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, message, Modal, Radio, Select, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  MessageOutlined,
  SendOutlined,
  TrophyOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { broadcastAPI, classAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { TextArea } = Input;

const initialFormValues = {
  targetClass: undefined,
  content: '',
  displayMode: 'banner'
};

const FloatingContactButton = () => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [form] = Form.useForm();
  const { user } = useAuth();

  const requireLogin = () => {
    if (!localStorage.getItem('token')) {
      message.warning('请先登录后再使用快捷功能');
      return false;
    }
    return true;
  };

  const loadClasses = useCallback(async () => {
    if (!localStorage.getItem('token')) {
      message.warning('请先登录后再发送广播');
      return;
    }

    try {
      setClassesLoading(true);
      const response = await classAPI.getAll({
        pageSize: 200
      });
      if (response.data.success) {
        const classList = Array.isArray(response.data.data) ? response.data.data : [];
        setClasses(classList);
        if (classList.length === 1) {
          form.setFieldsValue({ targetClass: classList[0].id });
        }
      } else {
        message.error(response.data.message || '获取班级列表失败');
      }
    } catch (error) {
      console.error('load broadcast classes failed:', error);
      message.error('获取班级列表失败');
    } finally {
      setClassesLoading(false);
    }
  }, [form]);

  useEffect(() => {
    if (modalOpen) {
      loadClasses();
    }
  }, [loadClasses, modalOpen]);

  const openQuickBroadcast = () => {
    if (!requireLogin()) return;
    form.setFieldsValue(initialFormValues);
    setModalOpen(true);
  };

  const openShortcut = (path) => {
    if (!requireLogin()) return;
    navigate(path);
  };

  const handleSend = async () => {
    try {
      const values = await form.validateFields();
      setSending(true);
      await broadcastAPI.sendMessage({
        type: 'text',
        content: values.content.trim(),
        displayMode: values.displayMode,
        targetClass: values.targetClass
      });
      message.success('广播发送成功');
      setModalOpen(false);
      form.resetFields();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('quick broadcast send failed:', error);
      message.error(error.response?.data?.message || '广播发送失败');
    } finally {
      setSending(false);
    }
  };

  const shortcutButtonStyle = (background, shadowColor) => ({
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    border: 0,
    background,
    boxShadow: `0 4px 12px ${shadowColor}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  });

  const shortcutLabelStyle = {
    background: 'rgba(0, 0, 0, 0.72)',
    color: 'white',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    whiteSpace: 'nowrap',
    pointerEvents: 'none'
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          right: '20px',
          bottom: '20px',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px'
        }}
      >
        <Tooltip title={'\u89c6\u9891\u770b\u73ed'} placement="left">
          <button
            type="button"
            aria-label="video-class-monitor"
            onClick={() => openShortcut('/smart-attendance?tab=video')}
            style={shortcutButtonStyle('#13c2c2', 'rgba(19, 194, 194, 0.28)')}
          >
            <VideoCameraOutlined style={{ color: 'white', fontSize: '23px' }} />
          </button>
        </Tooltip>
        <div style={shortcutLabelStyle}>{'\u89c6\u9891\u770b\u73ed'}</div>

        <Tooltip title={'\u79ef\u5206\u64cd\u4f5c'} placement="left">
          <button
            type="button"
            aria-label="points-operation"
            onClick={() => openShortcut('/students/points-new')}
            style={shortcutButtonStyle('#d48806', 'rgba(212, 136, 6, 0.28)')}
          >
            <TrophyOutlined style={{ color: 'white', fontSize: '23px' }} />
          </button>
        </Tooltip>
        <div style={shortcutLabelStyle}>{'\u79ef\u5206\u64cd\u4f5c'}</div>

        <Tooltip title={'\u8003\u52e4\u64cd\u4f5c'} placement="left">
          <button
            type="button"
            aria-label="attendance-operation"
            onClick={() => openShortcut('/smart-attendance?tab=manual')}
            style={shortcutButtonStyle('#52c41a', 'rgba(82, 196, 26, 0.28)')}
          >
            <CheckCircleOutlined style={{ color: 'white', fontSize: '23px' }} />
          </button>
        </Tooltip>
        <div style={shortcutLabelStyle}>{'\u8003\u52e4\u64cd\u4f5c'}</div>

        <Tooltip title="快速发送广播" placement="left">
          <button
            type="button"
            aria-label="快速发送广播"
            onClick={openQuickBroadcast}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              border: 0,
              background: '#1677ff',
              boxShadow: hovered
                ? '0 6px 20px rgba(22, 119, 255, 0.46)'
                : '0 4px 12px rgba(22, 119, 255, 0.28)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: hovered ? 'scale(1.08)' : 'scale(1)',
              transition: 'all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
              cursor: 'pointer'
            }}
          >
            <MessageOutlined style={{ color: 'white', fontSize: '26px' }} />
          </button>
        </Tooltip>

        <div
          style={{
            background: 'rgba(0, 0, 0, 0.72)',
            color: 'white',
            padding: '3px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            whiteSpace: 'nowrap',
            pointerEvents: 'none'
          }}
        >
          快速广播
        </div>
      </div>

      <Modal
        title="快速发送广播"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>
            取消
          </Button>,
          <Button key="send" type="primary" icon={<SendOutlined />} loading={sending} onClick={handleSend}>
            立即发送
          </Button>
        ]}
        width={420}
        centered
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={initialFormValues}>
          <Form.Item
            name="targetClass"
            label="发送到班级"
            rules={[{ required: true, message: '请选择班级' }]}
          >
            <Select
              showSearch
              loading={classesLoading}
              placeholder="请选择班级"
              optionFilterProp="label"
              options={classes.map((item) => ({
                value: item.id,
                label: item.name || `${item.grade || ''}${item.class_name || ''}` || `班级 ${item.id}`
              }))}
            />
          </Form.Item>

          <Form.Item
            name="content"
            label="广播内容"
            rules={[
              { required: true, message: '请输入广播内容' },
              {
                validator: (_, value) => {
                  if (!String(value || '').trim()) return Promise.reject(new Error('请输入广播内容'));
                  return Promise.resolve();
                }
              }
            ]}
          >
            <TextArea rows={4} maxLength={500} showCount placeholder="请输入要发送到教室的广播内容" />
          </Form.Item>

          <Form.Item name="displayMode" label="显示方式">
            <Radio.Group>
              <Radio.Button value="banner">横幅</Radio.Button>
              <Radio.Button value="popup">弹窗</Radio.Button>
              <Radio.Button value="fullscreen">全屏</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default FloatingContactButton;
